import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Check, ArrowRight, AlertTriangle, Settings as SettingsIcon } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const FEATURES = [
  "Projets illimités",
  "Toutes les fonctionnalités incluses",
  "Mode hors-ligne complet (PWA)",
  "IA pour études et reconnaissance OCR factures",
  "Tous les exports PDF pro",
  "Webhook Make.com pour alertes",
  "Espace artisan inclus",
  "Support par email direct",
  "Mises à jour à vie",
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState('monthly'); // monthly | yearly
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    axios.get(`${API}/billing/status`, { withCredentials: true })
      .then((r) => setBilling(r.data))
      .catch(() => {});
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur Stripe');
      setLoading(false);
    }
  };

  const openPortal = async () => {
    try {
      const res = await axios.post(`${API}/billing/portal`, {
        return_url: window.location.origin + '/parametres',
      }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur Stripe Portal');
    }
  };

  const isTrialing = billing?.is_trial_active;
  const isSubscribed = billing?.is_subscribed;
  const isLegacy = billing?.is_legacy_admin;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
      <header className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-zinc-950" />
            </div>
            <span className="font-bold text-lg">Home <span className="text-amber-400">MDB 2A</span></span>
          </Link>
          <Link to="/app" className="text-sm text-zinc-400 hover:text-white">← Retour à l'app</Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-3xl">
          {/* Trial / Status banner */}
          {billing && !isSubscribed && !isLegacy && (
            <div className={`mb-8 p-4 rounded-xl border flex items-start gap-3 ${isTrialing ? 'bg-blue-500/5 border-blue-500/30' : 'bg-amber-500/10 border-amber-500/40'}`}>
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isTrialing ? 'text-blue-400' : 'text-amber-400'}`} />
              <div className="text-sm">
                {isTrialing ? (
                  <>
                    <p className="font-semibold text-blue-300">Essai gratuit en cours</p>
                    <p className="text-zinc-400">Il te reste <strong>{billing.trial_days_left} jour{billing.trial_days_left > 1 ? 's' : ''}</strong>. Active ton abonnement pour continuer après.</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-amber-300">Ton essai gratuit est terminé</p>
                    <p className="text-zinc-300">Active ton abonnement pour retrouver l'accès à tous tes projets, études et données.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {isSubscribed && (
            <div className="mb-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-semibold text-emerald-300">Abonnement actif ✓</p>
                <p className="text-zinc-400">Statut : {billing.subscription_status}. Prochaine échéance : {billing.current_period_end ? new Date(billing.current_period_end).toLocaleDateString('fr-FR') : 'à définir'}.</p>
              </div>
              <button onClick={openPortal} className="text-sm text-amber-400 hover:text-amber-300 inline-flex items-center gap-1.5">
                <SettingsIcon className="w-4 h-4" /> Gérer
              </button>
            </div>
          )}

          <div className="text-center mb-10">
            <p className="text-amber-400 text-sm font-semibold uppercase tracking-wider mb-3">Tarif fondateur</p>
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">{isSubscribed ? 'Ton abonnement' : 'Choisis ton plan'}</h1>
            <p className="mt-3 text-zinc-400">Simple. Tout inclus. Sans engagement.</p>
          </div>

          {/* Plan toggle */}
          {!isSubscribed && !isLegacy && (
            <div className="flex justify-center mb-8">
              <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-full p-1">
                <button
                  onClick={() => setPlan('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${plan === 'monthly' ? 'bg-amber-500 text-zinc-950' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="plan-monthly"
                >
                  Mensuel
                </button>
                <button
                  onClick={() => setPlan('yearly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${plan === 'yearly' ? 'bg-amber-500 text-zinc-950' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="plan-yearly"
                >
                  Annuel <span className="text-xs ml-1 opacity-80">-16%</span>
                </button>
              </div>
            </div>
          )}

          {/* Pricing card */}
          <div className="rounded-3xl bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-amber-500/30 p-8 lg:p-12 shadow-2xl shadow-amber-500/10">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2">Solo MDB</h3>
              <p className="text-zinc-400">Tout l'outil pour un seul prix</p>
              <div className="mt-6 flex items-baseline justify-center gap-2">
                <span className="text-6xl font-bold text-amber-400">{plan === 'monthly' ? '39€' : '390€'}</span>
                <span className="text-zinc-500 text-lg">/{plan === 'monthly' ? 'mois' : 'an'}</span>
              </div>
              {plan === 'yearly' && <p className="text-sm text-emerald-400 mt-2">≈ 32,50€/mois • 2 mois offerts</p>}
            </div>

            <ul className="space-y-3 mb-8 max-w-md mx-auto">
              {FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-amber-400" />
                  </div>
                  <span className="text-zinc-200">{f}</span>
                </li>
              ))}
            </ul>

            {isLegacy ? (
              <div className="text-center text-zinc-400 italic">Compte fondateur — accès illimité offert.</div>
            ) : isSubscribed ? (
              <button
                onClick={openPortal}
                className="w-full py-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold transition-colors"
                data-testid="manage-subscription"
              >
                Gérer mon abonnement (Stripe Portal)
              </button>
            ) : (
              <button
                onClick={subscribe}
                disabled={loading}
                className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold text-lg transition-all hover:shadow-xl hover:shadow-amber-500/30 flex items-center justify-center gap-2"
                data-testid="subscribe-cta"
              >
                {loading ? 'Redirection vers Stripe...' : (<>S'abonner — {plan === 'monthly' ? '39€/mois' : '390€/an'} <ArrowRight className="w-5 h-5" /></>)}
              </button>
            )}
            {!isSubscribed && !isLegacy && (
              <p className="text-center text-sm text-zinc-500 mt-4">
                Paiement sécurisé via Stripe · Annulable en 1 clic · TVA incluse
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
